import { FImage, FRichText } from '@belong/contentful';
import { logger } from '@belong/logging/logger';
import { Document } from '@belong/types/cms';
import { NotUndefinedOrNull, ISupportCategory } from '@belong/types';
import { IArticleHeader } from '@belong/ui-components/components/contentBlocks/ArticleHeader/ArticleHeader';
import FArticleHeader from '@belong/ui-sections/sections/sectionArticleHeader/model';
import { FArticleTemplate } from 'src/components/sections/SectionSupportArticle/templates';
import CategoryModel from './Categories';

export interface IArticle {
  id: string;
  categories: (string | ISupportCategory)[];
  title: string;
  promoted: boolean;
  metaPageSlug: string;
  metaPageTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  metaNoIndex?: boolean;
  header?: IArticleHeader;
  content?: Document;
}

// Inject valid category objects
export const transformCategories = (model: CategoryModel, articleId: string, ids: string[]): ISupportCategory[] => {
  return ids
    .map(id => {
      const category = model.getCategoryById(id);
      if (!category) {
        logger.error(new Error(`No matching category for id: ${id} in article ${articleId}`));
      }
      return category;
    })
    .filter(Boolean as any as NotUndefinedOrNull);
};

function FSupportArticleContentAPI(data: any, categoryModel: CategoryModel): IArticle {
  const { categories, articleHeader, articleTemplate, featuredImage, articleContent, promoted, ...articleProps } =
    data.fields;

  return {
    id: data.sys.id,
    ...articleProps,
    categories: transformCategories(categoryModel, data.sys.id, categories),
    header: FArticleHeader(articleHeader),
    template: FArticleTemplate(articleTemplate),
    content: articleContent && FRichText(articleContent).html,
    featuredImage: featuredImage && FImage(featuredImage),
    promoted: !!promoted
  };
}

function FSupportArticleGraphQL(data: any, categoryModel: CategoryModel): IArticle {
  const { categories, ...fields } = data;

  return {
    ...fields,
    categories: transformCategories(categoryModel, data.id, categories),
    promoted: !!data.promoted
  };
}

export function FSupportArticle(data: any, categoryModel: CategoryModel): IArticle {
  // ContentAPI will return fields
  if (data.fields) {
    return FSupportArticleContentAPI(data, categoryModel);
  }

  // GraphQL API returns flat structure
  return FSupportArticleGraphQL(data, categoryModel);
}

export function FSupportArticles(data: any[], categoryModel: CategoryModel): IArticle[] {
  return data.map(item => FSupportArticle(item, categoryModel));
}
